import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui-helpers";
import { toast } from "sonner";
import { Database, Eye, Upload, CheckCircle2, AlertTriangle, X, ArrowRight } from "lucide-react";

const SLOTS = [
  { key: "clients", label: "Clients", hint: "Officio client export — e.g. clients.xlsx", accept: ".xlsx,.xls,.csv" },
  { key: "cases", label: "Cases", hint: "Officio case export — e.g. cases.csv", accept: ".csv,.xlsx,.xls" },
  { key: "tasks", label: "Tasks", hint: "Officio task export — e.g. tasks.xlsx", accept: ".xlsx,.xls,.csv" },
  { key: "notes", label: "Notes", hint: "Officio note export — e.g. notes.xlsx", accept: ".xlsx,.xls,.csv" },
];

const ROWS = [
  { key: "clients", label: "Clients" },
  { key: "applications", label: "Cases" },
  { key: "tasks", label: "Tasks" },
  { key: "notes", label: "Notes" },
];

function FileSlot({ slot, file, onPick, onClear, disabled }) {
  const ref = useRef(null);
  return (
    <div data-testid={`officio-slot-${slot.key}`} className={`rounded-xl border p-3 transition-colors ${file ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{slot.label}</div>
          <div className="text-[11px] text-slate-500 truncate">{file ? file.name : slot.hint}</div>
        </div>
        {file ? (
          <button data-testid={`officio-clear-${slot.key}`} onClick={() => { onClear(); if (ref.current) ref.current.value = ""; }} disabled={disabled} className="text-slate-400 hover:text-rose-600 shrink-0 disabled:opacity-40" title="Remove file"><X className="h-4 w-4" /></button>
        ) : null}
      </div>
      <input ref={ref} type="file" accept={slot.accept} className="hidden" data-testid={`officio-input-${slot.key}`} onChange={(e) => onPick(e.target.files?.[0] || null)} />
      <button data-testid={`officio-pick-${slot.key}`} onClick={() => ref.current?.click()} disabled={disabled} className="mt-2.5 w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary transition-colors disabled:opacity-50">
        {file ? "Choose a different file" : "Choose file"}
      </button>
    </div>
  );
}

export default function OfficioMigration() {
  const [files, setFiles] = useState({ clients: null, cases: null, tasks: null, notes: null });
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const chosen = SLOTS.filter((s) => files[s.key]);

  const pick = (key, file) => { setFiles((f) => ({ ...f, [key]: file })); setPreview(null); setResult(null); };

  const buildForm = () => {
    const fd = new FormData();
    chosen.forEach((s) => fd.append(s.key, files[s.key]));
    return fd;
  };

  const run = async (mode) => {
    if (!chosen.length) { toast.error("Attach at least one Officio export file"); return; }
    if (mode === "import" && !window.confirm("Import this data into the live CRM? Existing records matched by Officio ID / file number will be updated, not duplicated.")) return;
    setBusy(mode);
    try {
      const { data } = await api.post(mode === "preview" ? "/officio/preview" : "/officio/import", buildForm());
      if (mode === "preview") { setPreview(data); setResult(null); toast.success("Dry run complete — nothing was saved"); }
      else { setResult(data); toast.success("Officio data imported"); }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const summary = result || preview;
  const total = summary ? ROWS.reduce((n, r) => n + (summary[r.key]?.create || 0) + (summary[r.key]?.update || 0), 0) : 0;

  return (
    <Card className="p-5 space-y-4 mt-5" data-testid="officio-migration-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0"><Database className="h-5 w-5" /></div>
          <div>
            <h3 className="font-head font-bold text-slate-900">Officio Data Migration</h3>
            <p className="text-xs text-slate-500 max-w-2xl mt-0.5">Bring your legacy Officio records into this CRM. Always run a <strong>Preview / Dry Run</strong> first — it reads the files and reports exactly what would change without touching the database. Re-running the import is safe: records are matched on Officio client ID, file number and content keys, so nothing is duplicated.</p>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {SLOTS.map((slot) => (
          <FileSlot key={slot.key} slot={slot} file={files[slot.key]} disabled={!!busy}
            onPick={(f) => pick(slot.key, f)} onClear={() => pick(slot.key, null)} />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
        <button data-testid="officio-dry-run-btn" onClick={() => run("preview")} disabled={!!busy || !chosen.length}
          className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-opacity disabled:opacity-50">
          <Eye className="h-4 w-4" /> {busy === "preview" ? "Running dry run..." : "Preview / Dry Run"}
        </button>
        {preview && !result && (
          <button data-testid="officio-import-btn" onClick={() => run("import")} disabled={!!busy}
            className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-opacity disabled:opacity-50">
            <Upload className="h-4 w-4" /> {busy === "import" ? "Importing..." : "Import Officio Data"}
          </button>
        )}
        <span className="text-xs text-slate-400" data-testid="officio-selected-count">
          {chosen.length ? `${chosen.length} file${chosen.length > 1 ? "s" : ""} attached` : "No files attached yet"}
        </span>
      </div>

      {summary && (
        <div data-testid="officio-results" className={`rounded-xl border p-4 space-y-3 ${result ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/60"}`}>
          <div className="flex items-center gap-2">
            {result ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Eye className="h-4 w-4 text-slate-500" />}
            <div className="text-sm font-semibold text-slate-900" data-testid="officio-results-title">
              {result ? "Import complete — data is now live in your CRM" : "Dry run preview — nothing has been saved yet"}
            </div>
          </div>

          <div className="overflow-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Record type</th>
                  <th className="text-right px-3 py-2">{result ? "Created" : "To create"}</th>
                  <th className="text-right px-3 py-2">{result ? "Updated (matched existing)" : "To update (already exists)"}</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => (
                  <tr key={r.key} className="border-t border-slate-100" data-testid={`officio-row-${r.key}`}>
                    <td className="px-3 py-2 font-medium text-slate-700">{r.label}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700" data-testid={`officio-${r.key}-create`}>{summary[r.key]?.create ?? 0}</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-600" data-testid={`officio-${r.key}-update`}>{summary[r.key]?.update ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-slate-500" data-testid="officio-total">{total} record{total === 1 ? "" : "s"} in this run.</div>

          {(summary.unmatched?.tasks > 0 || summary.unmatched?.notes > 0) && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="officio-unmatched">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{summary.unmatched.tasks} task(s) and {summary.unmatched.notes} note(s) are not yet linked to a case or client — they are still imported, with their original Officio reference kept in the record.{!result && " During a dry run the cases don't exist yet, so most of these will link up automatically once you run the real import."}</span>
            </div>
          )}

          {(summary.warnings || []).length > 0 && (
            <ul className="text-xs text-slate-500 list-disc pl-5 space-y-0.5" data-testid="officio-warnings">
              {summary.warnings.map((w, i) => <li key={i}>{String(w)}</li>)}
            </ul>
          )}

          {result && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Link to="/admin/clients" data-testid="officio-goto-clients" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary transition-colors">View Clients <ArrowRight className="h-3.5 w-3.5" /></Link>
              <Link to="/admin/applications" data-testid="officio-goto-cases" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary transition-colors">View Cases <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
